import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { COLORS } from '../theme';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtCurrency(n) {
  return '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function aiRecBg(rec) {
  if (rec === 'approve') return '#4CAF50';
  if (rec === 'review')  return '#FF9800';
  if (rec === 'deny')    return '#f44336';
  return '#9e9e9e';
}

function aiRecLabel(rec) {
  if (rec === 'approve') return 'Approve';
  if (rec === 'review')  return 'Review';
  if (rec === 'deny')    return 'Deny';
  return 'Pending AI evaluation';
}

// ─── Small components ─────────────────────────────────────────────────────────

function StatCard({ label, value }) {
  return (
    <div style={{
      flex: 1, backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`,
      borderRadius: '8px', padding: '16px', textAlign: 'center',
    }}>
      <div style={{ fontSize: '24px', fontWeight: '700', color: COLORS.accent, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>{label}</div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '13px', color: value ? COLORS.textPrimary : COLORS.textMuted, fontFamily: mono ? 'monospace' : 'inherit' }}>{value || '—'}</div>
    </div>
  );
}

function AmbStatusBadge({ status }) {
  const map = {
    approved:  { bg: '#34A85326', text: COLORS.success,        label: 'Approved' },
    pending:   { bg: '#F5A62326', text: COLORS.warning,        label: 'Pending' },
    suspended: { bg: '#EA433526', text: COLORS.danger,         label: 'Suspended' },
    denied:    { bg: '#EA433526', text: COLORS.danger,         label: 'Denied' },
  };
  const s = map[status] || { bg: COLORS.surfaceHover, text: COLORS.textSecondary, label: status || '—' };
  return <span style={{ backgroundColor: s.bg, color: s.text, borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: '600' }}>{s.label}</span>;
}

function ReviewBadge({ status }) {
  const map = {
    approved: { bg: '#34A85326', text: COLORS.success, label: 'Approved' },
    denied:   { bg: '#EA433526', text: COLORS.danger,  label: 'Denied' },
    pending:  { bg: '#F5A62326', text: COLORS.warning, label: 'Pending' },
  };
  const s = map[status] || { bg: COLORS.surfaceHover, text: COLORS.textSecondary, label: status || '—' };
  return <span style={{ backgroundColor: s.bg, color: s.text, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '600' }}>{s.label}</span>;
}

function RewardStatusBadge({ status }) {
  const map = {
    paid:    { bg: '#34A85326', text: COLORS.success, label: 'Paid' },
    pending: { bg: '#F5A62326', text: COLORS.warning, label: 'Pending' },
  };
  const s = map[status] || { bg: COLORS.surfaceHover, text: COLORS.textSecondary, label: status || '—' };
  return <span style={{ backgroundColor: s.bg, color: s.text, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '600' }}>{s.label}</span>;
}

function Check({ val }) {
  return val
    ? <span style={{ color: COLORS.success, fontSize: '14px', fontWeight: '700' }}>✓</span>
    : <span style={{ color: COLORS.textMuted, fontSize: '14px' }}>✗</span>;
}

function AiRecCell({ r, expanded, onToggle }) {
  const colMap = { qualify: COLORS.success, deny: COLORS.danger, review: COLORS.warning };
  if (!r.aiRecommendation) return <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>—</span>;
  return (
    <span
      onClick={e => { e.stopPropagation(); onToggle(); }}
      style={{ cursor: 'pointer', color: colMap[r.aiRecommendation] || COLORS.textSecondary, fontSize: '12px', fontWeight: '600', userSelect: 'none' }}
    >
      {r.aiRecommendation}
      {r.aiConfidence && <span style={{ color: COLORS.textMuted, fontWeight: '400', fontSize: '11px' }}> ({r.aiConfidence})</span>}
      <span style={{ marginLeft: '4px', fontSize: '10px', color: COLORS.textMuted }}>{expanded ? '▲' : '▼'}</span>
    </span>
  );
}

const INPUT_STYLE = {
  color: '#1F2937', backgroundColor: 'white', border: '1px solid #D1D5DB',
  padding: '8px 12px', borderRadius: '6px', fontSize: '14px',
  width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
};

// ─── Column definitions ───────────────────────────────────────────────────────

const REF_COL = '1.4fr 0.85fr 52px 52px 68px 0.85fr 1.1fr 90px 130px';
const REF_HEADERS = ['Referred User', 'Signed Up', 'Email', 'Phone', 'Profile', 'First Job', 'AI Rec', 'Status', 'Actions'];

const REW_COL = '90px 80px 80px 90px 1.2fr 1fr 1fr 100px';
const REW_HEADERS = ['ID', 'Amount', 'Qualified', 'Status', 'Payout', 'Created', 'Paid', 'Actions'];

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AmbassadorDetailPage() {
  const { uid } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  const [activeTab, setActiveTab] = useState('referrals');

  const [referrals, setReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(true);
  const [referredNames, setReferredNames] = useState({});
  const fetchedNamesRef = useRef(new Set());
  const [expandedReasons, setExpandedReasons] = useState(new Set());

  const [rewards, setRewards] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(true);

  const [actionLoading, setActionLoading] = useState({});

  // modal: { type: 'approve'|'deny'|'pay', id, amount?, payoutMethod?, payoutHandle? }
  const [modal, setModal] = useState(null);
  const [modalInput, setModalInput] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  const [toast, setToast] = useState('');

  // Approve/deny application state
  const [approveDenyLoading, setApproveDenyLoading] = useState(false);
  const [approveDenyError, setApproveDenyError] = useState('');
  const [showDenyReason, setShowDenyReason] = useState(false);
  const [denyReason, setDenyReason] = useState('');

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  }

  // User doc — live
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'users', uid), snap => {
      if (!snap.exists()) { setNotFound(true); setLoading(false); return; }
      setUser({ id: snap.id, ...snap.data() });
      setLoading(false);
    });
    return () => unsub();
  }, [uid]);

  // Referrals — live
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'ambassador_referrals'), where('ambassadorUid', '==', uid)),
      snap => {
        const docs = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.signedUpAt?.toMillis?.() || 0) - (a.signedUpAt?.toMillis?.() || 0));
        setReferrals(docs);
        setReferralsLoading(false);

        // Secondary reads for referred user display names (deduped)
        const newUids = docs.map(d => d.referredUid).filter(id => id && !fetchedNamesRef.current.has(id));
        if (newUids.length > 0) {
          newUids.forEach(id => fetchedNamesRef.current.add(id));
          Promise.all(
            newUids.map(id =>
              getDoc(doc(db, 'users', id)).then(s => [
                id,
                s.exists() ? (s.data().displayName || s.data().email || id.slice(0, 8)) : id.slice(0, 8),
              ])
            )
          ).then(entries => setReferredNames(prev => ({ ...prev, ...Object.fromEntries(entries) })));
        }
      }
    );
    return () => unsub();
  }, [uid]);

  // Rewards — live
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'ambassador_rewards'), where('ambassadorUid', '==', uid)),
      snap => {
        setRewards(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))
        );
        setRewardsLoading(false);
      }
    );
    return () => unsub();
  }, [uid]);

  function handleCopy(text) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function toggleReason(id) {
    setExpandedReasons(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function openModal(type, id, extra = {}) {
    setModal({ type, id, ...extra });
    setModalInput('');
    setModalError('');
  }
  function closeModal() { setModal(null); setModalInput(''); setModalError(''); }

  async function handleApprove() {
    setModalLoading(true); setModalError('');
    try {
      await httpsCallable(functions, 'reviewReferral')({ referralId: modal.id, decision: 'approved' });
      showToast('Referral approved');
      closeModal();
    } catch (err) {
      setModalError(err.message || 'Action failed');
    } finally { setModalLoading(false); }
  }

  async function handleDeny() {
    if (!modalInput.trim()) { setModalError('Reason is required'); return; }
    setModalLoading(true); setModalError('');
    try {
      await httpsCallable(functions, 'reviewReferral')({ referralId: modal.id, decision: 'denied', reason: modalInput.trim() });
      showToast('Referral denied');
      closeModal();
    } catch (err) {
      setModalError(err.message || 'Action failed');
    } finally { setModalLoading(false); }
  }

  async function handlePay() {
    if (!modalInput) { setModalError('Password is required'); return; }
    setModalLoading(true); setModalError('');
    try {
      const res = await httpsCallable(functions, 'processRewardPayout')({ rewardId: modal.id, password: modalInput });
      showToast(`Paid ${fmtCurrency(res.data.amount)} via ${res.data.payoutMethod}`);
      closeModal();
    } catch (err) {
      setModalInput('');
      setModalError(err.message || 'Action failed. Check password.');
    } finally { setModalLoading(false); }
  }

  async function handleRegen(referralId) {
    const key = `regen_${referralId}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));
    try {
      await httpsCallable(functions, 'regenerateReferralRecommendation')({ referralId });
      showToast('AI recommendation regenerated');
    } catch (err) {
      showToast('Failed: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  }

  async function handleApproveApplication() {
    setApproveDenyLoading(true);
    setApproveDenyError('');
    try {
      await httpsCallable(functions, 'approveAmbassador')({ uid, action: 'approve' });
      // onSnapshot on the user doc picks up the status change automatically
    } catch (err) {
      setApproveDenyError(err.message || 'Action failed');
    } finally {
      setApproveDenyLoading(false);
    }
  }

  async function handleDenyApplication() {
    if (!denyReason.trim() || denyReason.trim().length < 10) {
      setApproveDenyError('Denial reason must be at least 10 characters');
      return;
    }
    setApproveDenyLoading(true);
    setApproveDenyError('');
    try {
      await httpsCallable(functions, 'approveAmbassador')({ uid, action: 'deny', denyReason: denyReason.trim() });
    } catch (err) {
      setApproveDenyError(err.message || 'Action failed');
    } finally {
      setApproveDenyLoading(false);
    }
  }

  if (loading) return <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Loading…</div>;
  if (notFound) return <div style={{ color: COLORS.danger, fontSize: '13px' }}>Ambassador not found.</div>;

  const amb = user.ambassador || {};
  const displayName = user.displayName || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || '(No name)';

  const card = { backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '20px', marginBottom: '20px' };
  const sectionLabel = { fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '14px' };
  const headerCell = { fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 0', fontWeight: '600' };
  const cell = { padding: '11px 0', fontSize: '12px', color: COLORS.textPrimary };
  const actionBtn = color => ({ background: 'none', border: 'none', color, fontSize: '12px', cursor: 'pointer', padding: '0 8px 0 0', fontWeight: '500' });

  return (
    <div>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: COLORS.success, color: 'white',
          padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
          zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>{toast}</div>
      )}

      {/* Modal */}
      {modal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ backgroundColor: COLORS.surface, borderRadius: '12px', padding: '28px', width: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>

            {modal.type === 'approve' && (
              <>
                <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary, marginBottom: '8px' }}>Approve Referral</div>
                <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '20px' }}>
                  Mark this referral as qualified. This increments the ambassador's qualified count and may trigger a reward payout.
                </div>
              </>
            )}

            {modal.type === 'deny' && (
              <>
                <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary, marginBottom: '8px' }}>Deny Referral</div>
                <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '16px' }}>Provide a reason for the denial.</div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>Reason *</label>
                  <textarea
                    value={modalInput}
                    onChange={e => setModalInput(e.target.value)}
                    placeholder="Explain why this referral is being denied…"
                    rows={3}
                    style={{ ...INPUT_STYLE, resize: 'vertical' }}
                    autoFocus
                  />
                </div>
              </>
            )}

            {modal.type === 'pay' && (
              <>
                <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary, marginBottom: '8px' }}>Process Payout</div>
                <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '6px' }}>
                  Pay <strong style={{ color: COLORS.textPrimary }}>{fmtCurrency(modal.amount)}</strong> to{' '}
                  <strong style={{ color: COLORS.textPrimary }}>@{modal.payoutHandle}</strong> via{' '}
                  <strong style={{ color: COLORS.textPrimary }}>{modal.payoutMethod}</strong>.
                </div>
                <div style={{ fontSize: '12px', color: COLORS.warning, marginBottom: '16px' }}>This action is irreversible and will be logged.</div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>Your Password *</label>
                  <input type="password" autoComplete="current-password" value={modalInput} onChange={e => setModalInput(e.target.value)} style={INPUT_STYLE} autoFocus />
                </div>
              </>
            )}

            {modalError && <div style={{ fontSize: '12px', color: COLORS.danger, marginBottom: '12px' }}>{modalError}</div>}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={closeModal} disabled={modalLoading} style={{ padding: '8px 16px', fontSize: '13px', border: `1px solid ${COLORS.border}`, borderRadius: '6px', backgroundColor: 'transparent', color: COLORS.textSecondary, cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={modal.type === 'approve' ? handleApprove : modal.type === 'deny' ? handleDeny : handlePay}
                disabled={modalLoading}
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: '500', border: 'none', borderRadius: '6px', cursor: 'pointer',
                  backgroundColor: modal.type === 'deny' ? COLORS.danger : modal.type === 'pay' ? COLORS.accent : COLORS.success,
                  color: 'white', opacity: modalLoading ? 0.6 : 1,
                }}
              >
                {modalLoading ? '…' : modal.type === 'approve' ? 'Approve' : modal.type === 'deny' ? 'Deny' : 'Confirm Payout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Back */}
      <button onClick={() => navigate('/ambassadors')} style={{ background: 'none', border: 'none', color: COLORS.accent, fontSize: '13px', cursor: 'pointer', padding: 0, fontWeight: '500', marginBottom: '20px', display: 'block' }}>
        ← Ambassadors
      </button>

      {/* Name + status */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <div style={{ fontSize: '22px', fontWeight: '700', color: COLORS.textPrimary }}>{displayName}</div>
          <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: '4px' }}>{user.email || '—'}</div>
        </div>
        <AmbStatusBadge status={amb.status} />
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '20px' }}>
        <StatCard label="Total Referred" value={amb.totalReferred ?? 0} />
        <StatCard label="Qualified" value={amb.qualifiedReferred ?? 0} />
        <StatCard label="Rewards Pending" value={fmtCurrency(amb.rewardsPending)} />
        <StatCard label="Rewards Paid" value={fmtCurrency(amb.rewardsPaid)} />
      </div>

      {/* Info card */}
      <div style={card}>
        <div style={sectionLabel}>Ambassador Info</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 28px' }}>
          <Field label="Ambassador ID" value={amb.ambassadorId} mono />
          <Field label="Referral Code" value={amb.referralCode} mono />
          <Field label="Applied At" value={fmtDate(amb.appliedAt)} />
          <Field label="Community" value={amb.community} />
          <Field label="Trade" value={amb.trade} />
          <Field label="Approved At" value={fmtDate(amb.approvedAt)} />
        </div>

        {/* Referral URL */}
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '6px' }}>Referral URL</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '12px', color: COLORS.textSecondary, fontFamily: 'monospace' }}>{amb.referralUrl || '—'}</span>
            {amb.referralUrl && (
              <button onClick={() => handleCopy(amb.referralUrl)} style={{ background: 'none', border: `1px solid ${COLORS.border}`, borderRadius: '4px', padding: '2px 10px', fontSize: '11px', cursor: 'pointer', color: copied ? COLORS.success : COLORS.textSecondary }}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            )}
          </div>
        </div>

        {/* Why Ambassador */}
        {amb.whyAmbassador && (
          <div>
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '6px' }}>Why Ambassador</div>
            <div style={{ fontSize: '13px', color: COLORS.textPrimary, lineHeight: '1.5', backgroundColor: COLORS.bg, borderRadius: '6px', padding: '10px 14px', border: `1px solid ${COLORS.border}30` }}>
              {amb.whyAmbassador}
            </div>
          </div>
        )}
      </div>

      {/* ── PENDING APPLICATION REVIEW ───────────────────────────────── */}
      {amb.status === 'pending' && (
        <div style={{ marginBottom: '20px' }}>

          {/* AI Recommendation Card */}
          <div style={card}>
            <div style={sectionLabel}>AI Recommendation</div>
            <div style={{ marginBottom: '10px' }}>
              <span style={{
                backgroundColor: aiRecBg(amb.aiRecommendation),
                color: 'white', borderRadius: '4px', padding: '3px 10px',
                fontSize: '12px', fontWeight: '600',
              }}>
                {aiRecLabel(amb.aiRecommendation)}
              </span>
            </div>
            <div style={{ fontSize: '13px', color: COLORS.textSecondary, lineHeight: '1.6' }}>
              {amb.aiReason || 'AI evaluation not yet available'}
            </div>
            {amb.aiConfidence && (
              <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginTop: '8px' }}>
                Confidence: {amb.aiConfidence.charAt(0).toUpperCase() + amb.aiConfidence.slice(1)}
              </div>
            )}
            {amb.aiEvaluatedAt && (
              <div style={{ fontSize: '11px', color: COLORS.textMuted, marginTop: '4px' }}>
                Evaluated: {fmtDate(amb.aiEvaluatedAt)}
              </div>
            )}
          </div>

          {/* Application Details Card */}
          <div style={card}>
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '8px' }}>
              Why they want to be an ambassador
            </div>
            <div style={{
              fontSize: '13px', color: COLORS.textPrimary, lineHeight: '1.6',
              backgroundColor: COLORS.bg, borderRadius: '6px', padding: '10px 14px',
              border: `1px solid ${COLORS.border}30`, marginBottom: '14px',
            }}>
              {amb.whyAmbassador || '—'}
            </div>
            <div style={{ display: 'flex', gap: '24px', fontSize: '13px' }}>
              <div>
                <span style={{ fontWeight: '600', color: COLORS.textPrimary }}>Community: </span>
                <span style={{ color: COLORS.textSecondary }}>{amb.community || '—'}</span>
              </div>
              <div>
                <span style={{ fontWeight: '600', color: COLORS.textPrimary }}>Trade: </span>
                <span style={{ color: COLORS.textSecondary }}>{amb.trade || '—'}</span>
              </div>
            </div>
          </div>

          {/* Action Buttons Card */}
          <div style={card}>
            {!showDenyReason ? (
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleApproveApplication}
                  disabled={approveDenyLoading}
                  style={{
                    padding: '8px 20px', backgroundColor: '#4CAF50', color: 'white',
                    border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                    cursor: approveDenyLoading ? 'default' : 'pointer',
                    opacity: approveDenyLoading ? 0.6 : 1,
                  }}
                >
                  {approveDenyLoading ? '…' : 'Approve Application'}
                </button>
                <button
                  onClick={() => { setShowDenyReason(true); setApproveDenyError(''); }}
                  disabled={approveDenyLoading}
                  style={{
                    padding: '8px 20px', backgroundColor: '#f44336', color: 'white',
                    border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                    cursor: approveDenyLoading ? 'default' : 'pointer',
                    opacity: approveDenyLoading ? 0.6 : 1,
                  }}
                >
                  Deny Application
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={denyReason}
                  onChange={e => setDenyReason(e.target.value)}
                  placeholder="Reason for denial (min 10 characters)…"
                  autoFocus
                  style={{ ...INPUT_STYLE, flex: 1, width: 'auto' }}
                />
                <button
                  onClick={handleDenyApplication}
                  disabled={approveDenyLoading || denyReason.trim().length < 10}
                  style={{
                    padding: '8px 16px', backgroundColor: '#f44336', color: 'white',
                    border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                    cursor: 'pointer', whiteSpace: 'nowrap',
                    opacity: (approveDenyLoading || denyReason.trim().length < 10) ? 0.5 : 1,
                  }}
                >
                  {approveDenyLoading ? '…' : 'Submit Denial'}
                </button>
                <button
                  onClick={() => { setShowDenyReason(false); setDenyReason(''); setApproveDenyError(''); }}
                  disabled={approveDenyLoading}
                  style={{
                    padding: '8px 14px', backgroundColor: 'transparent', color: COLORS.textSecondary,
                    border: `1px solid ${COLORS.border}`, borderRadius: '6px', fontSize: '13px',
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
            {approveDenyError && (
              <div style={{ fontSize: '12px', color: COLORS.danger, marginTop: '8px' }}>
                {approveDenyError}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.border}`, marginBottom: '16px' }}>
        {['referrals', 'rewards'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 20px', fontSize: '13px',
            fontWeight: activeTab === tab ? '600' : '400',
            color: activeTab === tab ? COLORS.accent : COLORS.textSecondary,
            borderBottom: activeTab === tab ? `2px solid ${COLORS.accent}` : '2px solid transparent',
            marginBottom: '-1px', textTransform: 'capitalize',
          }}>
            {tab === 'referrals' ? `Referrals (${referrals.length})` : `Rewards (${rewards.length})`}
          </button>
        ))}
      </div>

      {/* ── REFERRALS TAB ────────────────────────────────────────────────── */}
      {activeTab === 'referrals' && (
        <div style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflowX: 'auto' }}>
          <div style={{ minWidth: '920px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: REF_COL, padding: '0 16px', borderBottom: `1px solid ${COLORS.border}` }}>
              {REF_HEADERS.map(h => <div key={h} style={headerCell}>{h}</div>)}
            </div>

            {referralsLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: REF_COL, padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}30` }}>
                  {Array.from({ length: 9 }).map((__, j) => (
                    <div key={j} style={{ height: '13px', backgroundColor: COLORS.surfaceHover, borderRadius: '4px', width: '70%', animation: 'pulse 1.4s ease-in-out infinite' }} />
                  ))}
                </div>
              ))
            ) : referrals.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: COLORS.textMuted, fontSize: '13px' }}>No referrals yet.</div>
            ) : (
              referrals.map((r, i) => (
                <div key={r.id}>
                  <div style={{
                    display: 'grid', gridTemplateColumns: REF_COL, padding: '0 16px', alignItems: 'center',
                    borderBottom: expandedReasons.has(r.id) ? 'none' : (i < referrals.length - 1 ? `1px solid ${COLORS.border}30` : 'none'),
                  }}>
                    <div style={{ ...cell, overflow: 'hidden' }}>
                      <div style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {referredNames[r.referredUid] || '…'}
                      </div>
                    </div>
                    <div style={{ ...cell, color: COLORS.textSecondary, fontSize: '11px' }}>{fmtDate(r.signedUpAt)}</div>
                    <div style={{ ...cell, textAlign: 'center' }}><Check val={r.emailVerified} /></div>
                    <div style={{ ...cell, textAlign: 'center' }}><Check val={r.phoneVerified} /></div>
                    <div style={{ ...cell, textAlign: 'center' }}><Check val={r.profileComplete} /></div>
                    <div style={{ ...cell, color: COLORS.textSecondary, fontSize: '11px' }}>{fmtDate(r.firstJobAppliedAt)}</div>
                    <div style={cell}><AiRecCell r={r} expanded={expandedReasons.has(r.id)} onToggle={() => toggleReason(r.id)} /></div>
                    <div style={cell}><ReviewBadge status={r.reviewStatus} /></div>
                    <div style={{ ...cell, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                      {r.reviewStatus === 'pending' && <>
                        <button onClick={() => openModal('approve', r.id)} style={actionBtn(COLORS.success)}>Approve</button>
                        <button onClick={() => openModal('deny', r.id)} style={actionBtn(COLORS.danger)}>Deny</button>
                      </>}
                      <button
                        onClick={() => handleRegen(r.id)}
                        disabled={!!actionLoading[`regen_${r.id}`]}
                        title="Re-run AI recommendation"
                        style={{ ...actionBtn(COLORS.textSecondary), opacity: actionLoading[`regen_${r.id}`] ? 0.5 : 1 }}
                      >
                        {actionLoading[`regen_${r.id}`] ? '…' : 'AI ↺'}
                      </button>
                    </div>
                  </div>

                  {/* Expanded AI reason */}
                  {expandedReasons.has(r.id) && (
                    <div style={{
                      padding: '6px 16px 14px',
                      borderBottom: i < referrals.length - 1 ? `1px solid ${COLORS.border}30` : 'none',
                      backgroundColor: COLORS.bg + 'aa',
                    }}>
                      <div style={{ fontSize: '11px', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '4px' }}>AI Reasoning</div>
                      <div style={{ fontSize: '12px', color: COLORS.textSecondary, lineHeight: '1.5' }}>
                        {r.aiRecommendationReason || 'No reason provided.'}
                      </div>
                      {r.aiRecommendedAt && (
                        <div style={{ fontSize: '11px', color: COLORS.textMuted, marginTop: '4px' }}>Evaluated {fmtDate(r.aiRecommendedAt)}</div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── REWARDS TAB ──────────────────────────────────────────────────── */}
      {activeTab === 'rewards' && (
        <div style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflowX: 'auto' }}>
          <div style={{ minWidth: '700px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: REW_COL, padding: '0 16px', borderBottom: `1px solid ${COLORS.border}` }}>
              {REW_HEADERS.map(h => <div key={h} style={headerCell}>{h}</div>)}
            </div>

            {rewardsLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: REW_COL, padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}30` }}>
                  {Array.from({ length: 8 }).map((__, j) => (
                    <div key={j} style={{ height: '13px', backgroundColor: COLORS.surfaceHover, borderRadius: '4px', width: '70%', animation: 'pulse 1.4s ease-in-out infinite' }} />
                  ))}
                </div>
              ))
            ) : rewards.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: COLORS.textMuted, fontSize: '13px' }}>No rewards yet.</div>
            ) : (
              rewards.map((r, i) => (
                <div key={r.id} style={{
                  display: 'grid', gridTemplateColumns: REW_COL, padding: '0 16px', alignItems: 'center',
                  borderBottom: i < rewards.length - 1 ? `1px solid ${COLORS.border}30` : 'none',
                }}>
                  <div style={{ ...cell, fontFamily: 'monospace', fontSize: '11px', color: COLORS.textMuted }}>{r.id.slice(0, 8)}…</div>
                  <div style={{ ...cell, fontWeight: '600', color: COLORS.accent }}>{fmtCurrency(r.amount)}</div>
                  <div style={{ ...cell, color: COLORS.textSecondary }}>{r.qualifiedCount ?? '—'}</div>
                  <div style={cell}><RewardStatusBadge status={r.status} /></div>
                  <div style={{ ...cell, fontSize: '12px', color: COLORS.textSecondary }}>
                    {r.payoutMethod ? `${r.payoutMethod} @${r.payoutHandle}` : '—'}
                  </div>
                  <div style={{ ...cell, fontSize: '12px', color: COLORS.textSecondary }}>{fmtDate(r.createdAt)}</div>
                  <div style={{ ...cell, fontSize: '12px', color: COLORS.textSecondary }}>{fmtDate(r.paidAt)}</div>
                  <div style={cell}>
                    {r.status === 'pending' && (
                      <button
                        onClick={() => openModal('pay', r.id, { amount: r.amount, payoutMethod: r.payoutMethod, payoutHandle: r.payoutHandle })}
                        style={actionBtn(COLORS.accent)}
                      >
                        Pay
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
